import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";

const HEADER_LABELS = {
  iban: "IBAN",
  wallet: "SIPAY CUZDAN",
  auto: "TALİMAT",
  history: "GEÇMİŞ",
  car: "ARAÇ",
  profile: "PROFİL",
  default: "+",
};

const OPEN_TRANSITION = { duration: 0.22, ease: [0.22, 1, 0.36, 1] };
const CLOSE_TRANSITION = { duration: 0.16, ease: [0.4, 0, 1, 1] };

function HeaderLabel({ variant }) {
  const label = HEADER_LABELS[variant] || HEADER_LABELS.default;
  const className = `expandable-event-card__header-icon${
    variant === "wallet" ? " expandable-event-card__header-icon--compact" : ""
  }`;
  return (
    <span className={className} aria-hidden="true">
      {label}
    </span>
  );
}

function CardHeader({ headerClass, headerVariant, imageSrc }) {
  return (
    <div className={headerClass}>
      {imageSrc ? (
        <img src={imageSrc} alt="" className="expandable-event-card__img" />
      ) : (
        <div className="expandable-event-card__header-inner">
          <HeaderLabel variant={headerVariant} />
        </div>
      )}
    </div>
  );
}

export default function ExpandableEventCard({
  cardId,
  imageSrc,
  headerVariant = "default",
  title = "Baslik",
  description = "",
  content,
  locked = false,
  lockedLabel = "Bakımda",
}) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const headerClass = `expandable-event-card__header expandable-event-card__header--${headerVariant}`;
  const modalKey = cardId || title;

  const overlay =
    !locked && typeof document !== "undefined"
      ? createPortal(
          <AnimatePresence mode="wait">
            {isOpen ? (
              <div className="expandable-event-card__overlay-wrap" key={`overlay-${modalKey}`}>
                <motion.button
                  type="button"
                  className="expandable-event-card__backdrop"
                  aria-label="Kapat"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={CLOSE_TRANSITION}
                  onClick={() => setIsOpen(false)}
                />
                <motion.div
                  className="expandable-event-card__modal"
                  role="dialog"
                  aria-modal="true"
                  aria-label={title}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 16 }}
                  transition={OPEN_TRANSITION}
                >
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="expandable-event-card__close"
                    aria-label="Kapat"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>

                  <CardHeader
                    headerClass={`${headerClass} expandable-event-card__header--modal`}
                    headerVariant={headerVariant}
                    imageSrc={imageSrc}
                  />

                  <div className="expandable-event-card__modal-body">
                    <h3 className="expandable-event-card__modal-title">{title}</h3>
                    {description ? (
                      <p className="expandable-event-card__modal-desc">{description}</p>
                    ) : null}
                    <motion.div
                      className="expandable-event-card__content"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      transition={{ duration: 0.18, delay: 0.04, ease: "easeOut" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {content}
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            ) : null}
          </AnimatePresence>,
          document.body
        )
      : null;

  return (
    <>
      <div
        className={`expandable-event-card${locked ? " expandable-event-card--locked" : ""}`}
        role="button"
        tabIndex={locked ? -1 : 0}
        aria-disabled={locked ? "true" : "false"}
        onClick={() => {
          if (!locked) setIsOpen(true);
        }}
        onKeyDown={(e) => {
          if (locked) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen(true);
          }
        }}
      >
        <CardHeader headerClass={headerClass} headerVariant={headerVariant} imageSrc={imageSrc} />
        <div className="expandable-event-card__body">
          <h3 className="expandable-event-card__title">{title}</h3>
          {description ? <p className="expandable-event-card__desc">{description}</p> : null}
        </div>
        {locked ? (
          <div className="expandable-event-card__lock" role="status">
            <span className="expandable-event-card__lock-label">{lockedLabel}</span>
          </div>
        ) : null}
      </div>
      {overlay}
    </>
  );
}
